import type { CSSProperties } from 'react';
import { CheckIcon } from '@phosphor-icons/react';

import { cn } from '../lib/cn';

export type StepperStep = { id: string; label: string };

export function Stepper({
  steps,
  currentId,
  completedIds,
  onStepClick,
}: {
  steps: StepperStep[];
  currentId: string;
  completedIds: Set<string>;
  onStepClick?: (id: string) => void;
}) {
  const currentIdx = steps.findIndex((s) => s.id === currentId);
  return (
    <div className="flex w-full items-center justify-center">
      {steps.map((s, i) => {
        const isCurrent = s.id === currentId;
        const isCompleted = completedIds.has(s.id) && !isCurrent;
        const isVisited = isCompleted || completedIds.has(s.id) || i <= currentIdx;
        const clickable = !!onStepClick && isVisited && !isCurrent;
        let markerBackground = 'var(--border-strong)';
        if (isCompleted) {
          markerBackground = 'var(--teal-primary)';
        } else if (isCurrent) {
          markerBackground = 'var(--status-report-text)';
        }

        const markerStyle = {
          background: markerBackground,
          '--tw-ring-color': isCurrent ? 'var(--status-report-text)' : 'transparent',
        } as CSSProperties;

        return (
          <div
            key={s.id}
            className="flex items-center"
            style={{ flex: i < steps.length - 1 ? 1 : '0 0 auto' }}
          >
            <button
              type="button"
              disabled={!clickable || undefined}
              onClick={() => {
                if (clickable) onStepClick(s.id);
              }}
              className={cn(
                'group inline-flex items-center gap-2',
                clickable ? 'cursor-pointer' : 'cursor-default',
              )}
            >
              <span
                className={cn(
                  'grid h-3 w-3 shrink-0 place-items-center rounded-full transition-colors',
                  isCurrent && 'ring-2 ring-offset-2',
                )}
                style={markerStyle}
              >
                {isCompleted ? <CheckIcon size={8} weight="bold" color="white" /> : null}
              </span>
              <span
                className={cn(
                  'text-[13px] whitespace-nowrap transition-colors',
                  isCurrent ? 'text-text-primary font-semibold' : 'text-text-secondary',
                  clickable && 'group-hover:text-text-primary',
                )}
              >
                {s.label}
              </span>
            </button>
            {i < steps.length - 1 && (
              <span
                className="mx-4 h-px flex-1"
                style={{ background: 'var(--border-default)', minWidth: 40 }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
